import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DesignRequest } from '@ghostscreen/contracts';
import { formatList } from '../../i18n';
import {
  formatSentDate,
  STATUS_PILL,
  STATUS_STEP_INDEX,
  STATUS_STEPS,
  statusNote,
} from './designFormat';
import { DesignProofPreview } from './DesignProofPreview';

type DesignRequestCardProps = {
  request: DesignRequest;
  brand: string;
  screenNames: string[];
  photoUrl: string | null;
  busy?: boolean;
  onApprove: () => void;
  onRequestChanges: (changeRequest: string) => void;
};

export function DesignRequestCard({
  request,
  brand,
  screenNames,
  photoUrl,
  busy = false,
  onApprove,
  onRequestChanges,
}: DesignRequestCardProps) {
  const { t } = useTranslation(['designer', 'common']);
  const [changesOpen, setChangesOpen] = useState(false);
  const [changeText, setChangeText] = useState('');
  const pill = STATUS_PILL[request.status];
  const stepIndex = STATUS_STEP_INDEX[request.status];
  const isReview = request.status === 'review';
  const nfiles = request.attachments.length;

  return (
    <article
      className="flex flex-col gap-4 rounded-[14px] px-[22px] py-5"
      style={{
        background: 'var(--gs-panel)',
        border:
          request.status === 'review'
            ? '1.5px solid var(--gs-accent)'
            : '1px solid var(--gs-line)',
      }}
    >
      <div className="flex flex-wrap items-start justify-between gap-x-3.5 gap-y-2">
        <div className="flex min-w-[180px] flex-1 flex-col gap-[3px]">
          <span className="text-base font-extrabold tracking-[-0.015em]">{request.title}</span>
          <span className="text-[13px]" style={{ color: 'var(--gs-muted)' }}>
            {request.revision > 0
              ? t('card.metaRevised', {
                  kind: t(`kinds.${request.kind}`),
                  date: formatSentDate(request.createdAt),
                  count: nfiles,
                  version: request.revision + 1,
                })
              : t('card.meta', {
                  kind: t(`kinds.${request.kind}`),
                  date: formatSentDate(request.createdAt),
                  count: nfiles,
                })}
          </span>
        </div>
        <span
          className="flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12.5px] font-bold"
          style={{ color: pill.color, borderColor: 'var(--gs-line)' }}
        >
          <span className="h-[7px] w-[7px] rounded-full" style={{ background: pill.color }} />
          {t(`status.${request.status}`)}
        </span>
      </div>

      <div className="grid grid-cols-4 gap-1.5">
        {STATUS_STEPS.map((step, index) => {
          const on = index <= stepIndex;
          const barColor = on
            ? request.status === 'live'
              ? 'var(--gs-ok)'
              : 'var(--gs-accent)'
            : 'var(--gs-line)';
          return (
            <div key={step} className="flex flex-col gap-1.5">
              <span className="block h-1 rounded-sm" style={{ background: barColor }} />
              <span
                className={`text-[10px] font-bold sm:text-xs ${on ? '' : 'hidden sm:inline'}`}
                style={{ color: on ? 'var(--gs-ink2)' : 'var(--gs-muted)' }}
              >
                {t(`steps.${step}`)}
              </span>
            </div>
          );
        })}
      </div>

      <p className="m-0 text-sm leading-[1.45]" style={{ color: 'var(--gs-ink2)' }}>
        {statusNote({
          status: request.status,
          changeRequest: request.changeRequest,
          approvedAt: request.approvedAt,
          createdAt: request.createdAt,
          screenNames,
        })}
      </p>

      {isReview && request.proof ? (
        <div className="flex flex-col gap-3.5">
          <div
            className="rounded-xl border p-4"
            style={{ background: 'var(--gs-panel2)', borderColor: 'var(--gs-line)' }}
          >
            <DesignProofPreview
              proof={request.proof}
              orientation={request.orientation}
              brand={brand}
              photoUrl={photoUrl}
            />
          </div>
          <span className="text-[13px]" style={{ color: 'var(--gs-muted)' }}>
            {screenNames.length
              ? t('card.approveOnScreens', { screens: formatList(screenNames) })
              : t('card.approveNoScreens')}
          </span>

          {!changesOpen ? (
            <div className="flex flex-wrap gap-2.5">
              <button
                type="button"
                disabled={busy}
                onClick={onApprove}
                className="min-w-[180px] flex-1 rounded-[10px] border-none px-4 py-3 text-[15px] font-bold disabled:opacity-50"
                style={{
                  color: 'var(--gs-accent-ink)',
                  background: 'var(--gs-accent-solid)',
                  fontFamily: 'inherit',
                }}
              >
                {t('card.approve')}
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  setChangesOpen(true);
                  setChangeText('');
                }}
                className="min-w-[140px] flex-1 rounded-[10px] border px-4 py-3 text-[15px] font-bold disabled:opacity-50"
                style={{
                  color: 'var(--gs-ink)',
                  background: 'var(--gs-panel)',
                  borderColor: 'var(--gs-line)',
                  fontFamily: 'inherit',
                }}
              >
                {t('card.askChanges')}
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-2.5">
              <textarea
                value={changeText}
                onChange={(event) => setChangeText(event.target.value)}
                rows={3}
                placeholder={t('card.changesPlaceholder')}
                className="resize-y rounded-[10px] border px-[13px] py-3 text-[15px] leading-[1.45] outline-none focus:border-[var(--gs-accent)]"
                style={{
                  color: 'var(--gs-ink)',
                  background: 'var(--gs-field)',
                  borderColor: 'var(--gs-line)',
                  fontFamily: 'inherit',
                }}
              />
              <div className="flex flex-wrap gap-2.5">
                <button
                  type="button"
                  disabled={busy || !changeText.trim()}
                  onClick={() => {
                    onRequestChanges(changeText.trim());
                    setChangesOpen(false);
                    setChangeText('');
                  }}
                  className="rounded-[10px] border-none px-4 py-[11px] text-[14.5px] font-bold disabled:opacity-50"
                  style={{
                    color: 'var(--gs-accent-ink)',
                    background: 'var(--gs-accent-solid)',
                    fontFamily: 'inherit',
                  }}
                >
                  {t('card.sendChanges')}
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setChangesOpen(false)}
                  className="border-none bg-transparent px-1.5 py-[11px] text-[14.5px] font-semibold"
                  style={{ color: 'var(--gs-muted)', fontFamily: 'inherit' }}
                >
                  {t('common:actions.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </article>
  );
}
